<script>
import Header from './Header.vue';
import customTooltip from '@/utils/customTooltip.js';

export default {
  name: 'Home',
  components: {
    'header-component': Header,
  },
  mounted() {
    customTooltip();
  }

}
</script>
<template>
  <div role="main">
    <div class="top-wrap">
      <div class="top-content">
        <div class="top-banner">
          <header-component></header-component>
          <h1>Reshaping the Treatment<br><span>of Schizophrenia in Adults</span></h1>
          <section>
            <p>
              Schizophrenia is a chronic illness characterized by waves of stability and relapse. Antipsychotic medications can help improve symptoms and delay time to relapse.<sup>1</sup>
            </p>
            <p>
              Repeated relapses can have a number of consequences, including higher medical costs.<sup>2,3</sup> One strategy to delay relapse in some adult patients with schizophrenia is the earlier use of long-acting injectable antipsychotic (LAI) medication after diagnosis.
            </p>
            <p>
              LAIs are traditionally thought of as options for adult patients who had failed oral medications, had multiple relapses, or were nonadherent.<sup>4,5</sup> More recently, many payer, provider, and patient advocacy initiatives have supported earlier discussions of LAI use.<sup>1,5-8</sup>
            </p>
            <p>This website provides resources for payer and healthcare system leaders who want to implement change in their schizophrenia treatment strategy, which includes the consideration for earlier use of LAIs for adult patients with schizophrenia.</p>
          </section>
        </div>
      </div>

      <div class="resources">
          <h2 class="banner-footer">
            Explore these 4 sections to locate resources to support your efforts<br>and provide a coordinated approach to care.
          </h2>

        <div>
          <a href="/#/Population_Identification_Processes" class="custom-tooltip" aria-label="Population Identification Processes" >
            <div class="resource_info_wrap">
              <img src="../img/glass.png" alt="glass icon"/>
              <h2>
                Population Identification Processes
              </h2>
            </div>
            <button>VIEW RESOURCES ></button>
          </a>
        </div>
        <div>
          <a href="/#/Expanding_Internal_Awareness" class="custom-tooltip" aria-label="Expanding Internal Awareness">
            <div class="resource_info_wrap">
              <img src="../img/note.png" alt="note icon"/>
              <h2>
                Expanding Internal Awareness
              </h2>
            </div>
            <button>VIEW RESOURCES ></button>
          </a>
        </div>
        <div>
          <a href="/#/Collaborative_Care_Resources" class="custom-tooltip" aria-label="Collaborative Care Resources">
            <div class="resource_info_wrap">
              <img src="../img/hands.png" alt="hands icon"/>
              <h2>
                Collaborative Care Resources
              </h2>
            </div>
            <button>VIEW RESOURCES ></button>
          </a>
        </div>
        <div>
          <a href="/#/Process_Evaluation" class="custom-tooltip" aria-label="Process Evaluation">
            <div class="resource_info_wrap">
              <img src="../img/graphic.png" alt="graphic icon"/>
              <h2>
                Process Evaluation
              </h2>
            </div>
            <button>VIEW RESOURCES ></button>
          </a>
        </div>
      </div>
    </div>
    <div class="home-body footer-references">
      <p>
        <strong>References: 1.</strong> Keepers GA, Fochtmann LJ, Anzia JM, et al. <i>The American Psychiatric Association Practice Guideline for the Treatment of Patients With
           Schizophrenia.</i> Third edition. American Psychiatric Association; 2021. Accessed November 20, 2023. <span><a style="display: inline; color: #363636;word-break: break-all;" href="https://psychiatryonline.org/doi/pdf/10.1176/appi.books.9780890424841" target="_blank">https://psychiatryonline.org/doi/pdf/10.1176/
           appi.books.9780890424841</a></span> <strong>2.</strong> Lafeuille MH, Gravel J, Lefebvre P, et al. Patterns of relapse and associated cost burden in schizophrenia patients receiving<br>
            atypical antipsychotics. <i>J Med Econ</i>. 2013;16(11):1290-1299. <span>doi:10.3111/13696998.2013.841705</span> <strong>3.</strong> Karve SJ, Panish JM, Dirani RG, Candrilli SD. Health care<br>
             utilization and costs among Medicaid-enrolled patients with schizophrenia experiencing multiple psychiatric relapses. <i>Health Outcomes Res Med</i>. 2012;3(4):e183-
             e194. doi:10.1016/j.ehrm.2012.06.003 <strong>4.</strong> Brissos S, Veguilla MR, Taylor D, Balanzá-Martinez V. The role of long-acting injectable antipsychotics in schizophrenia: a
              critical appraisal. <i>Ther Adv Psychopharmacol</i>. 2014;4(5):198-219. doi:10.1177/2045125314540297 <strong>5.</strong> National Council for Mental Wellbeing. <i>Guide to Long-Acting<br>
               Medications for Clinicians and Organizations</i>. February 2, 2022. Accessed November 20, 2023. <span><a style="display: inline;color: #363636;word-break: break-all;" href="https://www.thenationalcouncil.org/wp-content/uploads/2021/12/2022.02.02_NC_Updated-Guide-To-LAMs.pdf" target="_blank">https://www.thenationalcouncil.org/wp-content/
               uploads/2021/12/2022.02.02_NC_Updated-Guide-To-LAMs.pdf</a></span> <strong>6.</strong> University of South Florida; Florida Medicaid Drug Therapy Management Program for<br>
                Behavioral Health. 2019–2020 Florida best practice psychotherapeutic medication guidelines for adults. January 2020. Accessed November 20, 2023. <span><a style="display: inline; color: #363636;word-break: break-all;" href="https://floridabhcenter.cbcs.usf.edu/wp-content/uploads/2021/04/2019-Psychotherapeutic-Medication-Guidelines-for-Adults-with-References_06-04-20.pdf" target="_blank">https://
                floridabhcenter.cbcs.usf.edu/wp-content/uploads/2021/04/2019-Psychotherapeutic-Medication-Guidelines-for-Adults-with-References_06-04-20.pdf</a></span> <strong>7.</strong> Beacon
                 Health Options. Addressing an underutilized best practice for severe and persistent mental illness: long-acting injectables. Beacon Health Options website. December 2017. Accessed November 20, 2023. <span><a style="display: inline;color: #363636;word-break: break-all;" href="https://s21151.pcdn.co/wp-content/uploads/Beacon-LAI-AP_FINAL.pdf" target="_blank">https://s21151.pcdn.co/wp-content/uploads/Beacon-LAI-AP_FINAL.pdf</a></span> <strong>8.</strong> Correll CU, Citrome L, Haddad PM, et<br>
                  al. The use of long-acting injectable antipsychotics in schizophrenia: evaluating the evidence. <i>J Clin Psychiatry</i>. 2016;77(suppl 3):1-24. doi:10.4088/JCP.15032su1
      </p>
    </div>

  </div>

</template>

<style scoped>
#app>div {
  width: 100%;
}

/*TOP */

.top-wrap {
  background: rgb(0, 18, 82);
  background: linear-gradient(0deg, rgba(0, 18, 82, 1) 0%, rgba(0, 70, 126, 1) 50%, rgba(0, 70, 126, 1) 100%);
  color: #ffffff;
  width: 100%;
}

.top-content {
  background: linear-gradient(to top, #0079A8, #0084B8);
  border-radius: 0 0 0 100px;
  margin-left: 3%;
  width: 97%;
  padding-bottom: 50px;
}

.top-banner {
  background: url(../img/banner-bg.png) no-repeat 0 0;
  background-size: cover;
  border-radius: 0 0 0 100px;
  margin-left: 1.5%;
  padding-bottom: 46px;
  width: 98.5%;
}

.top-banner h1 {
  display: block;
  font-size: 38px;
  font-family: "Gotham", sans-serif;
  color: #ffffff;
  font-weight: bold;
  line-height: 1.211;
  text-align: center;
  max-width: 1121px;
  margin: 68px auto 42px auto;

}

.top-banner h1 span {
  display: block;
  font-family: "GothamBook", sans-serif;
  font-weight: 400;
}

.top-banner section {
  color: #ffffff;
  font-size: 18px;
  font-family: "GothamBook", sans-serif;
  font-weight: 500;
  line-height: 24px;
  margin: 0 auto;
  max-width: 1030px;
  text-align: center;
}

.top-banner section p {
  margin: 1em auto;
}

.resources p.banner-footer {
  color: #ffffff;
  font-family: "GothamBook", sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  text-align: center;
  margin:47px auto;
}

.resources h2.banner-footer {
  width: 100%;
  text-align: center;
  font-family: "GothamBook", sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  text-align: center;
  margin: 0 0 47px auto;
}

.resources {
  display: block;
  margin: 47px auto 107px auto;
  width: 1150px;
}

.resources>div {
  padding-left: 16px;
  vertical-align: top;
  width: 266px;
}

.resources>div:first-child {
  padding-left: 0px;
}

.resources>div:last-child {
  width: 261px;
}

.resources>div:last-child .resource_info_wrap {
  border: 0;
  margin-right: 0;
  padding-right: 0;
}

.resources .resource_info_wrap {
  border-right: 1px solid #FFFFFF !important;
  margin: 0 15px 25px 0;
  padding-right: 16px;
  text-align: center;
  width: calc(100% - 10px);
}

.resources>div h2 {
  color: #FFFFFF;
  font-family: "GothamBook", sans-serif;
  font-size: 24px;
  height: 85px;
  line-height: 1.167;
  margin: 25px auto 0 auto;
  width: 180px;
}

.resources>div button {
  background: rgb(17, 66, 130);
  background: linear-gradient(90deg, rgba(17, 66, 130, 1) 0%, rgba(80, 52, 142, 1) 100%);
  border: 0;
  border-radius: 0 0 30px 0;
  color: #ffffff;
  cursor: pointer;
  font-family: "GothamBook", sans-serif;
  font-size: 18px;
  font-weight: 800;
  height: 55px;
  line-height: 55px;
  width: 261px;
}

.home-body {
  color: #000000;
  display: block;
  font-family: "GothamBook", sans-serif;
  font-size: 14px;
  line-height: 20px;
  margin: 50px auto 0 auto;
  width: 1121px;
}

.footer-references span {
  word-break: break-all;
  white-space: pre-line;
}

@supports (-webkit-touch-callout: none) {
  .resources>div button {
    line-height: 65px;
  }
}


@media screen and (max-width: 1024px) {
  .top-banner section {
    max-width: 95%;
  }

  footer img {
    left: 78%;
  }

  .top-banner *,
  .body * {
    max-width: 95%;
  }


  .resources {
    width: 700px;
  }

  .resources>div,
  .resources>div:last-child {
    height: 330px;
    text-align: center;
    width: 336px;
  }

  .resources>div:nth-child(2) .resource_info_wrap {
    border-right: 0;
  }

  .resources>div:nth-child(3) {
    padding-left: 0;
  }

  .resources>div button {
    width: 310px;
  }

  .home-body {
    max-width: 95%;
  }
}

@media screen and (max-width: 880px) {

  .top-banner {
    background: url(../img/banner-mobile2-bg.png) no-repeat 0 0;
    background-size: cover;
    border-radius: 0 0 0 100px;
    margin-left: 1.5%;
    padding-bottom: 46px;
    width: 98.5%;
  }

  .top-banner section {
    font-family: "GothamBook", sans-serif;
    font-size: 20px;
    line-height: 30px;
    font-weight: 100;
    width: 84%;
  }

  .top-banner section p {
    line-height: 20px;
  }

  .top-content p.banner-footer {
    font-size: 30px;
    margin: 24px auto;
    width: 73%;
  }

  .resources {
    margin: 80px auto 0 auto;
    width: 80%;
  }

  .resources>div {
    margin-right: 28px;
    margin-bottom: 70px;
    padding-left: 0;
    width: 297px;
  }

  .resources div:nth-child(2n) {
    margin-right: 0;
    width: 260px;
  }

  .resources div:nth-child(2n) .resource_info_wrap {
    border-right: 0;
    margin: 0 0 25px 0;
  }

  .resources .resource_info_wrap {
    margin: 0 37px 25px 0;
    padding-right: 0px;
    width: 100%;
  }

  .home-body {
    width: 87%;
  }



  .home a {
    background: url(../img/icon-menu-home.png) no-repeat left center;
  }

  .population-identification {
    background: url(../img/icon-menu-glass.png) no-repeat left center;
  }

  .expanding-capabilities {
    background: url(../img/icon-menu-expanding.png) no-repeat left center;
  }

  .collaborative-care {
    background: url(../img/icon-menu-hands.png) no-repeat left center;
  }

  .process-evaluation {
    background: url(../img/icon-menu-process.png) no-repeat left center;
  }

  .resource-library {
    background: url(../img/icon-menu-resources.png) no-repeat left center;
  }

  .site-guide {
    background: url(../img/icon-menu-book.png) no-repeat left center;
  }

  .menu li.site-guide {
    border: 0;
  }

  .menu li a {
    position: relative;
  }

  .menu li a::after {
    content: '>';
    color: #0f3765;
    font-size: 29px;
    font-weight: bold;
    position: absolute;
    right: 0;
  }

}

@media screen and (max-width: 540px) {

  .resources {
    margin: 40px auto 0 auto;
    text-align: center;
    width: 90%;
  }

  .resources .resource_info_wrap {
    border-right: 1px solid #667f9d;
    height: 125px;
    margin: 0 12px 25px 0;
    padding-right: 19px;
    width: calc(106% - 10px);
  }

  .resources div a {
    display: block;
    height: 55px;
  }

  .resources>div h2 {
    font-size: 12px;
    height: 50px;
    line-height: 14px;
    margin-bottom: 20px;
    width: 80%;
  }

  .resources .resource_info_wrap img {
    width: 38px;
  }

  .resources>div button {
    width: 148px;
    height: 33px;
    line-height: 33px;
    font-size: 9px;
    border-radius: 0 0 15px 0;
  }

  .resources>div,
  .resources div:nth-child(2n) {
    height: 200px;
    margin-bottom: 35px;
    text-align: center;
    width: 44%;
  }



  .top-banner h1 {
    font-size: 19px;
    line-height: 23px;
    max-width: 85%;
    margin: 40px auto 21px auto;
  }

  .top-banner h1 span {
    display: block;
    font-size: 19px;
    line-height: 23px;
  }

  .top-banner section {
    font-size: 13px;
    line-height: 16px;
  }

  .top-content p.banner-footer {
    font-size: 15px;
    line-height: 18px;
  }

}

@media screen and (max-width: 320px) {
  .resources>div button {
    width: 126px;
  }

  footer li[data-v-40ab164b]:nth-child(2n) {
    width: 42%;
  }
}

/*Tag Sup*/
.top-banner sup,
.bottom-banner sup {
  font-size: 11px;
  font-weight: 400;
  margin: 0 0 0 2px;
  vertical-align: text-bottom;
}

@media screen and (max-width: 880px) {
  .top-banner sup {
    vertical-align: 5px;
    line-height: 1;
  }
}

@media screen and (max-width: 540px) {

  .top-banner sup,
  .bottom-banner sup {
    font-size: 8px;
    line-height: unset;
    vertical-align: text-bottom;
  }
}

/*. Tag Sup*/
</style>



// WEBPACK FOOTER //
// src/components/Home.vue